import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import communityService from '../../../api/communityService';
import { setCommentUsage } from '../../../redux/slices/communityAllowanceSlice';
import {
  mapCommentsFromAPI,
  mapNewCommentFromAPI,
  mapCommentLikeResponseFromAPI,
  addCommentToList,
  updateCommentInList,
  deleteCommentFromList,
  toggleCommentLikeInList,
} from '../adapters';
import { toast } from 'sonner';
import { getUser } from '../../../utils/auth';
import { generateAvatarUrl } from '../utils/communityHelpers';

/**
 * Custom Hook: useComments
 * Manages comments state and operations for a specific post
 * Handles nested replies, likes, and CRUD operations
 */
const useComments = (postId) => {
  const dispatch = useDispatch();
  // Get current user from Redux store
  const currentUser = useSelector((state) => state.auth?.user);

  // State management
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    length: 10,
    total: 0,
    pages: 0,
  });



  /**
   * Count total comments including nested replies (recursive)
   * @param {Array} commentsList - Comments array
   * @returns {number} - Total count
   */
  const countAllComments = useCallback((commentsList) => {
    if (!Array.isArray(commentsList)) return 0;

    let count = 0;
    for (const comment of commentsList) {
      count += 1; // Count this comment
      if (comment.replies && Array.isArray(comment.replies)) {
        count += countAllComments(comment.replies); // Recursively count replies
      }
    }
    return count;
  }, []);

  /**
   * Fetch comments for the post
   * CRITICAL: Removed pagination from dependencies to prevent infinite loops
   * @param {Object} params - Query parameters
   */
  const fetchComments = useCallback(async (params = {}) => {
    try {
      if (!postId) return;

      setLoading(true);
      setError(null);

      const queryParams = {
        post_id: postId,
        page: params.page || pagination.page,
        length: params.length || pagination.length,
        ...(params.comment_id && { comment_id: params.comment_id }),
      };

      const response = await communityService.getPostComments(queryParams);

      if (response?.data?.data) {
        const { data: commentsData, total, page, length: perPage, pages } = response.data.data;

        // Map API response to UI format
        const mappedComments = mapCommentsFromAPI(commentsData || []);

        // Calculate actual total including nested replies
        const actualTotal = countAllComments(mappedComments);

       

        setComments(mappedComments);
        setPagination({
          page: page || 1,
          length: perPage || 10,
          total: actualTotal, // Use actual count including replies
          pages: pages || 0,
        });
      }
    } catch (err) {
      console.error('[fetchComments] Error:', err);
      setError(err?.response?.data?.message || 'Failed to load comments');
      // Don't show toast for comments errors to avoid spam
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, countAllComments]); // Only postId and countAllComments, NOT pagination!

  /**
   * Add a new comment or reply with Facebook-level optimistic updates
   * Flow:
   * 1. Create optimistic comment with temp ID
   * 2. Add to UI immediately
   * 3. Call API
   * 4. On success: replace temp with real comment
   * 5. On error: remove temp and show error
   * @param {FormData} commentFormData - Comment data
   * @param {number} parentId - Parent comment ID for replies
   */
  const addComment = useCallback(async (commentFormData, parentId = null) => {
    if (!commentFormData) return { success: false, error: 'No comment data' };

    // Generate unique temp ID
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const content = commentFormData.get('content');
    const imageFile = commentFormData.get('image_path');

    // Validate content - allow either text or image
    if ((!content || !content.trim()) && !imageFile) {
      toast.error('Comment cannot be empty');
      return { success: false, error: 'Empty content' };
    }

    // Get current user ID from localStorage
    const localUser = getUser();
    const currentUserId = localUser?.id;

    // Get user's actual profile image
    const userName = currentUser?.full_name || currentUser?.username || 'You';
    const userImage = currentUser?.profile_photo_path
      // ? `${process.env.REACT_APP_API_BASE_URL}/${currentUser.profile_photo_path}`
      // : `${process.env.REACT_APP_API_BASE_URL}/${currentUser.profile_photo_path}`;
            ? `${currentUser.profile_photo_path}`
      : `${currentUser.profile_photo_path}`;

    // Create preview URL for optimistic image
    let imagePreviewUrl = null;
    if (imageFile && imageFile instanceof File) {
      imagePreviewUrl = URL.createObjectURL(imageFile);
    }

   
    // Step 1: Create optimistic comment
    const optimisticComment = {
      id: tempId,
      author: userName,
      authorImage: userImage,
      content: content ? content.trim() : '', // Allow empty content if there's an image
      image: imagePreviewUrl, // Show preview immediately
      time: 'Just now',
      likes: 0,
      liked: false,
      userReaction: null,
      reactions: { like: 0, love: 0, haha: 0, wow: 0, sad: 0, angry: 0 },
      replies: [],
      parentId: parentId || null,
      isEditable: false, // Can't edit until confirmed by server
      isEditing: false,
      _optimistic: true, // Visual indicator
      _metadata: {
        postId,
        userId: currentUserId, // ✅ Use current user ID from localStorage
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };

    // Step 2: Add optimistically to UI
    setComments((prevComments) => {
      return addCommentToList(prevComments, optimisticComment, parentId);
    });

    setPagination((prev) => ({
      ...prev,
      total: prev.total + 1,
    }));

    // Step 3: Call API
    try {
      const response = await communityService.addComment(commentFormData);


      // Step 4: Replace with real comment from server
      if (response?.data?.data?.comment) {
        // Update the free-tier weekly allowance from the server's fresh count.
        if (response.data.data.usage) {
          dispatch(setCommentUsage(response.data.data.usage));
        }

        // Pass user data to preserve the correct avatar and name
        const userData = {
          name: userName,
          image: userImage,
        };
        const realComment = mapNewCommentFromAPI(response, userData);


        if (realComment) {
        

          setComments((prevComments) => {
          

            // Cleanup: Revoke blob URL from optimistic comment before replacing
            if (imagePreviewUrl) {
              URL.revokeObjectURL(imagePreviewUrl);
            }

            // Remove optimistic comment first
            let withoutTemp;

            if (!parentId) {
              // Top-level comment
              const before = prevComments.length;
              withoutTemp = prevComments.filter((c) => {
                const keep = c.id !== tempId;
                if (!keep) {
                }
                return keep;
              });
            } else {
              // Nested reply
              withoutTemp = updateCommentInList(prevComments, parentId, (parent) => {
                const filtered = (parent.replies || []).filter((r) => r.id !== tempId);
                return { replies: filtered };
              });
            }

            // Add real comment
            const final = addCommentToList(withoutTemp, realComment, parentId);
           
            return final;
          });

          toast.success(parentId ? 'Reply added!' : 'Comment added!');
          return { success: true, data: realComment };
        } else {
          console.warn('⚠️ [ADD COMMENT] Real comment mapping returned null!');
        }
      } else {
        console.warn('⚠️ [ADD COMMENT] No comment data in response structure!');
        console.warn('⚠️ Response structure:', {
          hasData: !!response?.data,
          hasDataData: !!response?.data?.data,
          hasComment: !!response?.data?.data?.comment,
          keys: Object.keys(response?.data || {})
        });
      }

      // If no comment data returned, keep optimistic
      console.warn('⚠️ [ADD COMMENT] Keeping optimistic state');
      return { success: true, data: optimisticComment };

    } catch (err) {
      // Step 5: Rollback on error
      console.error('Error adding comment:', err);

      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to add comment';

      toast.error(errorMessage);

      // Weekly comment allowance exhausted → sync the counter from the server.
      if (
        err?.response?.data?.errorType === 'COMMUNITY_COMMENT_LIMIT_REACHED' &&
        err?.response?.data?.data
      ) {
        dispatch(setCommentUsage(err.response.data.data));
      }

      // Cleanup: Revoke blob URL if it was created
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }

      // Remove optimistic comment
      setComments((prevComments) => {
        if (!parentId) {
          return prevComments.filter((c) => c.id !== tempId);
        } else {
          return updateCommentInList(prevComments, parentId, (parent) => ({
            replies: (parent.replies || []).filter((r) => r.id !== tempId),
          }));
        }
      });

      // Revert count
      setPagination((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
      }));

      return { success: false, error: errorMessage };
    }
  }, [postId, currentUser]);

  /**
   * Edit a comment
   * @param {FormData} editFormData - Edit data with comment ID
   */
  const editComment = useCallback(async (editFormData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await communityService.editComment(editFormData);

      if (response?.data?.data?.comment) {
        const updatedComment = response.data.data.comment;

        // Update comment in state
        setComments((prevComments) =>
          updateCommentInList(prevComments, updatedComment.id, {
            content: updatedComment.content,
            image: updatedComment.image_url,
            link_preview: updatedComment.link_preview || null,
            isEditing: false,
          })
        );

        toast.success('Comment updated successfully!');
        return { success: true };
      }
    } catch (err) {
      console.error('Error editing comment:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to edit comment';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a comment (cascades to replies)
   * @param {number} commentId - Comment ID
   */
  const deleteComment = useCallback(async (commentId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await communityService.deleteComment(commentId);

      if (response?.data) {
        // Remove comment from state
        setComments((prevComments) => deleteCommentFromList(prevComments, commentId));

        // Update pagination total
        setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));

        toast.success('Comment deleted successfully');
        return { success: true };
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to delete comment';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Like or unlike a comment with Facebook-level optimistic updates
   * CRITICAL: Do NOT sync with server after success - optimistic update is already correct!
   * Flow:
   * 1. Capture previous state for rollback
   * 2. Apply optimistic update immediately (this is the final state)
   * 3. Call API in background
   * 4. On error ONLY: rollback to previous state
   * @param {number} commentId - Comment ID
   */
  const toggleLikeComment = useCallback(async (commentId) => {
    if (!commentId) return;


    // Store snapshot of previous state for rollback
    let previousState = null;

    // Step 1: Apply optimistic update (THIS IS THE FINAL STATE - DON'T OVERWRITE IT!)
    setComments((prevComments) => {

      const updated = updateCommentInList(prevComments, commentId, (comment) => {
        // Capture previous state for rollback
        previousState = {
          liked: comment.liked,
          likes: comment.likes,
          userReaction: comment.userReaction,
          reactions: { ...(comment.reactions || {}) },
        };

        // Calculate new state
        const willBeLiked = !comment.liked;
        const currentLikes = parseInt(comment.likes) || 0;
        const newLikes = willBeLiked
          ? currentLikes + 1
          : Math.max(0, currentLikes - 1);

        

        // Return optimistic update - THIS IS FINAL!
        return {
          liked: willBeLiked,
          likes: newLikes,
          userReaction: willBeLiked ? 'like' : null,
          reactions: {
            ...(comment.reactions || {}),
            like: newLikes,
          },
        };
      });

      return updated;
    });

    // Step 2: Call API in background (DON'T update state on success!)
    try {
      const response = await communityService.likeUnlikeComment(commentId);


      // SUCCESS: Do nothing! The optimistic update is already correct.
      // The backend just confirms what we already showed the user.

    } catch (err) {
      // Step 3: ONLY on error - rollback to previous state
      console.error('❌ [API ERROR] Like toggle failed:', err);

      toast.error('Failed to update like');

      // Rollback to previous state
      if (previousState) {
        setComments((prevComments) => {
          return updateCommentInList(prevComments, commentId, previousState);
        });
      }
    }
  }, []);

  /**
   * Add or update a reaction to a comment (emoji reactions)
   * Uses optimistic updates - updates UI immediately, rolls back on error
   * @param {number} commentId - Comment ID
   * @param {string} reactionType - Reaction type ('like', 'love', 'haha', 'wow', 'sad', 'angry')
   */
  const addOrUpdateCommentReaction = useCallback(async (commentId, reactionType) => {
    if (!commentId || !reactionType) return;


    let previousState = null;

    // Step 1: Apply optimistic update
    setComments((prevComments) => {
      return updateCommentInList(prevComments, commentId, (comment) => {
        // Capture previous state for rollback
        previousState = {
          userReaction: comment.userReaction,
          reactions: { ...(comment.reactions || {}) },
          likes: comment.likes,
        };

        const currentReaction = comment.userReaction;
        const reactions = { ...(comment.reactions || { like: 0, love: 0, haha: 0, wow: 0, sad: 0, angry: 0 }) };

        // HYBRID CALCULATION: Calculate baseline legacy likes (from mobile app)
        const currentReactionTotal = Object.values(reactions).reduce((sum, count) => sum + count, 0);
        const legacyLikesBaseline = Math.max(0, comment.likes - currentReactionTotal);

        // Toggle behavior: if clicking same reaction, remove it
        if (currentReaction === reactionType) {
          // Remove reaction
          reactions[reactionType] = Math.max(0, (reactions[reactionType] || 0) - 1);
          const newReactionTotal = Object.values(reactions).reduce((sum, count) => sum + count, 0);
          const newTotalLikes = newReactionTotal + legacyLikesBaseline; // Preserve legacy likes


          return {
            userReaction: null,
            reactions,
            likes: newTotalLikes,
          };
        } else {
          // Add or change reaction
          if (currentReaction) {
            // Remove old reaction
            reactions[currentReaction] = Math.max(0, (reactions[currentReaction] || 0) - 1);
          }
          // Add new reaction
          reactions[reactionType] = (reactions[reactionType] || 0) + 1;
          const newReactionTotal = Object.values(reactions).reduce((sum, count) => sum + count, 0);
          const newTotalLikes = newReactionTotal + legacyLikesBaseline; // Preserve legacy likes


          return {
            userReaction: reactionType,
            reactions,
            likes: newTotalLikes,
          };
        }
      });
    });

    // Step 2: Call API
    try {
      const response = await communityService.addOrUpdateCommentReaction(commentId, reactionType);

      // SUCCESS: Do nothing! Optimistic update is already correct.

    } catch (err) {
      console.error('❌ [REACTION ERROR]:', err);
      toast.error('Failed to update reaction');

      // Rollback on error
      if (previousState) {
        setComments((prevComments) => {
          return updateCommentInList(prevComments, commentId, previousState);
        });
      }
    }
  }, []);

  /**
   * Remove reaction from a comment
   * @param {number} commentId - Comment ID
   */
  const removeCommentReaction = useCallback(async (commentId) => {
    if (!commentId) return;

    let previousState = null;

    // Apply optimistic update
    setComments((prevComments) => {
      return updateCommentInList(prevComments, commentId, (comment) => {
        previousState = {
          userReaction: comment.userReaction,
          reactions: { ...(comment.reactions || {}) },
          likes: comment.likes,
        };

        const reactions = { ...(comment.reactions || {}) };

        // HYBRID CALCULATION: Calculate baseline legacy likes (from mobile app)
        const currentReactionTotal = Object.values(reactions).reduce((sum, count) => sum + count, 0);
        const legacyLikesBaseline = Math.max(0, comment.likes - currentReactionTotal);

        if (comment.userReaction) {
          reactions[comment.userReaction] = Math.max(0, (reactions[comment.userReaction] || 0) - 1);
        }
        const newReactionTotal = Object.values(reactions).reduce((sum, count) => sum + count, 0);
        const newTotalLikes = newReactionTotal + legacyLikesBaseline; // Preserve legacy likes

        return {
          userReaction: null,
          reactions,
          likes: newTotalLikes,
        };
      });
    });

    // Call API
    try {
      await communityService.removeCommentReaction(commentId);
    } catch (err) {
      console.error('Error removing reaction:', err);
      toast.error('Failed to remove reaction');

      // Rollback on error
      if (previousState) {
        setComments((prevComments) => {
          return updateCommentInList(prevComments, commentId, previousState);
        });
      }
    }
  }, []);

  /**
   * Toggle edit mode for a comment
   * @param {number} commentId - Comment ID
   * @param {boolean} isEditing - Edit mode state
   */
  const toggleEditMode = useCallback((commentId, isEditing) => {
    setComments((prevComments) =>
      updateCommentInList(prevComments, commentId, { isEditing })
    );
  }, []);

  /**
   * Load more comments (pagination)
   */
  const loadMore = useCallback(async () => {
    if (pagination.page < pagination.pages) {
      await fetchComments({ page: pagination.page + 1 });
    }
  }, [fetchComments, pagination.page, pagination.pages]);

  /**
   * Refresh comments (reset to page 1)
   */
  const refresh = useCallback(async () => {
    await fetchComments({ page: 1 });
  }, [fetchComments]);

  return {
    // State
    comments,
    loading,
    error,
    pagination,

    // Actions
    fetchComments,
    addComment,
    editComment,
    deleteComment,
    toggleLikeComment,
    addOrUpdateCommentReaction,
    removeCommentReaction,
    toggleEditMode,
    loadMore,
    refresh,

    // Helpers
    hasMore: pagination.page < pagination.pages,
    totalComments: pagination.total,
  };
};

export default useComments;
